import { useState } from 'react';
import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import Paginate from '../../components/Paginate';
import {
  useGetLandlordsQuery,
  useDeleteLandlordMutation,
  useCreateLandlordMutation,
} from '../../slices/landlordsApiSlice';
import { useGetPaymentsQuery } from '../../slices/paymentsApiSlice';
import { useGetTotalRentByLandlordQuery } from '../../slices/rentsApiSlice';
import * as XLSX from 'xlsx';

const LandlordListScreen = () => {
  const { pageNumber } = useParams();

  const today = new Date().toISOString().split('T')[0];
  const jan1 = new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(jan1);
  const [endDate, setEndDate] = useState(today);
  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');

  const { data: landlordData, isLoading: loadingLandlords, error: landlordsError, refetch: refetchLandlords } = useGetLandlordsQuery({ pageNumber });
  const { data: paymentsData, isLoading: loadingPayments, error: paymentsError, refetch: refetchPayments } = useGetPaymentsQuery({ startDate, endDate });
  const { data: rentSummary, isLoading: loadingRents, error: rentError } = useGetTotalRentByLandlordQuery();

  const [deleteLandlord, { isLoading: loadingDelete }] = useDeleteLandlordMutation();
  const [createLandlord, { isLoading: loadingCreate }] = useCreateLandlordMutation();

  const handleSort = (field) => {
    const nextOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(nextOrder);
  };

  const deleteHandler = async (id) => {
    if (!window.confirm('Delete this landlord?')) return;
    try {
      await deleteLandlord(id).unwrap();
      toast.success('Landlord deleted');
      refetchLandlords();
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const createHandler = async () => {
    if (!window.confirm('Create default new landlord?')) return;
    try {
      await createLandlord({
        name: 'New Landlord',
        telephone: '0000000000',
        company: 'Default Company',
        serialNumber: '0000',
        address: 'Default Address',
      }).unwrap();
      toast.success('Landlord created');
      refetchLandlords();
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const filterAndSort = () => {
    let list = landlordData?.landlords || [];
    if (filter) {
      const term = filter.toLowerCase();
      list = list.filter(
        l =>
          l.name?.toLowerCase().includes(term) ||
          l.company?.toLowerCase().includes(term) ||
          l.telephone?.includes(filter)
      );
    }
    if (sortField) {
      list.sort((a, b) =>
        sortOrder === 'asc'
          ? a[sortField]?.localeCompare(b[sortField] || '')
          : b[sortField]?.localeCompare(a[sortField] || '')
      );
    }
    return list;
  };

  const sumPaymentsByAccountAndCategory = (accountName, category) =>
    paymentsData?.payments
      ?.filter(e => e.accountName === accountName && e.category === category)
      .reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0;

  const sumPaymentsByCategory = (category) =>
    paymentsData?.payments
      ?.filter(e => e.category === category)
      .reduce((sum, e) => sum + Number(e.amount || 0), 0) || 0;

  const rentMap = rentSummary?.reduce((acc, r) => {
    acc[r.landlordName] = r.totalRent;
    return acc;
  }, {}) || {};

  const totalRentPayment = sumPaymentsByCategory('RentPayment');
  const totalRentCommission = sumPaymentsByCategory('RentCommission');
  const totalBalanceDue = totalRentPayment + totalRentCommission;
  const totalRentAll = Object.values(rentMap).reduce((sum, val) => sum + val, 0);

  const exportToExcel = () => {
    const data = filterAndSort().map(l => {
      const credit = sumPaymentsByAccountAndCategory(l.name, 'RentPayment');
      const payment = sumPaymentsByAccountAndCategory(l.name, 'RentCommission');
      const balance = credit + payment;
      const totalRent = rentMap[l.name] || 0;

      return {
        ID: l._id,
        Name: l.name,
        Company: l.company,
        Telephone: l.telephone,
        Serial: l.serialNumber,
        Address: l.address,
        TotalRent: totalRent.toFixed(2),
        RentPayment: credit.toFixed(2),
        RentCommission: payment.toFixed(2),
        BalanceDue: balance.toFixed(2),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Landlords');
    XLSX.writeFile(workbook, 'LandlordList.xlsx');
  };

  if (loadingLandlords || loadingCreate || loadingDelete || loadingPayments || loadingRents) return <Loader />;
  if (landlordsError) return <Message variant="danger">{landlordsError.data?.message}</Message>;
  if (paymentsError) return <Message variant="danger">{paymentsError.data?.message}</Message>;
  if (rentError) return <Message variant="danger">{rentError.data?.message}</Message>;

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col><h1>Landlords</h1></Col>
        <Col className="text-end">
          <Button onClick={createHandler}><FaPlus /> Create Landlord</Button>{' '}
          <Button variant="success" onClick={exportToExcel}>📥 Export</Button>{' '}
          <Link to='/admin/landlordimportscreen' className='btn btn-light my-3'>AddViaExcel</Link>{' '}
          <Link to='/admin/landlordeditimportscreen' className='btn btn-light my-3'>EditViaExcel</Link>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by name, company, or phone"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Label>Start Date</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={e => {
              setStartDate(e.target.value);
              refetchPayments();
            }}
          />
        </Col>
        <Col md={3}>
          <Form.Label>End Date</Form.Label>
          <Form.Control
            type="date"
            value={endDate}
            onChange={e => {
              setEndDate(e.target.value);
              refetchPayments();
            }}
          />
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={4}><h5>Total RentPayment: {totalRentPayment.toFixed(2)}</h5></Col>
        <Col md={4}><h5>Total RentCommission: {totalRentCommission.toFixed(2)}</h5></Col>
        <Col md={4}><h5>BalanceDue: {totalBalanceDue.toFixed(2)}</h5></Col>
      </Row>

      <Row className="mb-4">
        <Col md={4}><h5>Total Rent: {totalRentAll.toFixed(2)}</h5></Col>
      </Row>

      <Table striped bordered hover responsive className="table-sm">
        <thead>
          <tr>
            <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>NAME</th>
            <th onClick={() => handleSort('company')} style={{ cursor: 'pointer' }}>COMPANY</th>
            <th onClick={() => handleSort('telephone')} style={{ cursor: 'pointer' }}>TELEPHONE</th>
            <th onClick={() => handleSort('serialNumber')} style={{ cursor: 'pointer' }}>SERIAL</th>
            <th>ADDRESS</th>
            <th>Total Rent</th>
            <th>Payment</th>
            <th>Commission</th>
            <th>BalanceDue</th>
            <th>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {filterAndSort().map(cust => {
            const credit = sumPaymentsByAccountAndCategory(cust.name, 'RentPayment');
            const payment = sumPaymentsByAccountAndCategory(cust.name, 'RentCommission');
            
            const totalRent = rentMap[cust.name] || 0;
            const balance = totalRent + (credit + payment);
            return (
              <tr key={cust._id}>
                <td>{cust.name}</td>
                <td>{cust.company}</td>
                <td>{cust.telephone}</td>
                <td>{cust.serialNumber}</td>
                <td>{cust.address}</td>
                <td>{totalRent.toFixed(2)}</td>
                <td>{credit.toFixed(2)}</td>
                <td>{payment.toFixed(2)}</td>
                <td style={{ color: balance > 0 ? 'red' : 'green' }}>
                  {balance.toFixed(2)}
                </td>
                <td>
                  <Button
                    as={Link}
                    to={`/admin/landlord/${cust._id}/edit`}
                    variant="light"
                    className="btn-sm me-2"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => deleteHandler(cust._id)}
                  >
                    <FaTrash style={{ color: 'white' }} />
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <Paginate
        pages={landlordData?.pages || 1}
        page={landlordData?.page || 1}
        isAdmin
        type="landlordlist"
      />
    </>
  );
};

export default LandlordListScreen;
            